import classNames from 'classnames';
import i18next from 'i18next';
import { castArray, every, isBoolean, isEmpty, get } from 'lodash-es';
import * as React from 'react';
import { FormState, UseFormGetValues } from 'react-hook-form';

const generate = (label: string, optional: boolean, ok: boolean) => {
  const labelClassNames = classNames('label', 'size-sm', 'bold', {
    'label-primary': ok,
    'label-danger': !optional && !ok,
    'label-gray': optional && !ok,
  });

  return (
    <label className="bold size-md">
      <span className={labelClassNames}>
        {ok
          ? i18next.t('register.form.label.ok')
          : optional
            ? i18next.t('register.form.label.optional')
            : i18next.t('register.form.label.required')}
      </span>
      <span className="m-l">{label}</span>
    </label>
  );
};

interface FormLabelOwnProps {
  label: string;
  optional?: boolean;
  optionalFieldNames?: string | string[]; // Specify when make only some attributes optional
  fieldNames: string | string[];
  getValues: UseFormGetValues<any>;
}

type FormLabelProps = FormLabelOwnProps & Partial<FormState<any>>;

export const FormLabel: React.FC<FormLabelProps> = (props) => {
  const { getValues, errors, fieldNames, label, optional, optionalFieldNames } =
    props;

  const optionalFieldNamesArray = castArray(optionalFieldNames);

  const ok = every(castArray(fieldNames), (fieldName) => {
    if (!isEmpty(get(errors, fieldName))) {
      return false;
    }

    const v = getValues(fieldName);
    if (isBoolean(v)) {
      return v; // !isEmpty(boolean) always returns false. https://lodash.com/docs/4.17.10#isEmpty
    }

    const isOptionalField = optionalFieldNamesArray.includes(fieldName);

    return isOptionalField || !isEmpty(v);
  });

  return generate(label, !!optional, ok);
};
FormLabel.displayName = 'FormLabel';

interface RawFormLabelOwnProps {
  label: string;
  optional?: boolean;
  requiredValues?: string | string[];
}

type RawFormLabelProps = RawFormLabelOwnProps;

export const RawFormLabel: React.FC<RawFormLabelProps> = (props) => {
  const { requiredValues, label, optional } = props;
  const ok = every(castArray(requiredValues), (v) => !isEmpty(v));

  return generate(label, !!optional, ok);
};
RawFormLabel.displayName = 'RawFormLabel';
